import React from 'react';
import Layout from '@/components/Layout/Layout';
import { Helmet } from 'react-helmet-async';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { FileText, Camera, Calendar, MapPin, Ruler, CheckCircle, AlertCircle } from 'lucide-react';
import { Link } from 'react-router-dom';

const DocumentationRequirementsPage = () => {
  return (
    <Layout>
      <Helmet>
        <title>Documentation Requirements for Insurance Claims | Carolina Trophy Roofs</title>
        <meta name="description" content="Essential documentation requirements for roof damage insurance claims in North Carolina. Learn what evidence is needed and how Carolina Trophy Roofs provides professional documentation services." />
        <meta name="keywords" content="insurance documentation, roof damage evidence, claim documentation, North Carolina insurance requirements, damage assessment" />
        <link rel="canonical" href="https://carolinatrophyroofs.com/resources/documentation-requirements" />
      </Helmet>

      <div className="container mx-auto py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          {/* Breadcrumb */}
          <nav className="mb-8">
            <Link to="/resources" className="text-primary hover:underline">Resources</Link>
            <span className="mx-2">→</span>
            <span className="text-muted-foreground">Documentation Requirements</span>
          </nav>

          {/* Header */}
          <div className="text-center mb-12">
            <Badge variant="outline" className="mb-4">
              <FileText className="mr-2 h-4 w-4" />
              Documentation Guide
            </Badge>
            <h1 className="text-4xl font-bold text-gray-900 mb-4">
              Documentation Requirements
            </h1>
            <p className="text-xl text-gray-600">
              Essential documentation for successful insurance claims
            </p>
          </div>

          {/* Main Content */}
          <div className="prose prose-lg max-w-none">
            <div className="bg-blue-50 border-l-4 border-blue-500 p-6 mb-8">
              <h2 className="text-xl font-semibold text-blue-800 mb-2">Professional Documentation Services</h2>
              <p className="text-blue-700">
                Carolina Trophy Roofs provides comprehensive documentation services as your <strong>Contractor of Record</strong>. 
                We use professional equipment and techniques to ensure your claim has the strongest possible foundation 
                with thorough, accurate documentation.
              </p>
            </div>

            <h2 className="text-2xl font-bold mb-6">Essential Documentation Categories</h2>
            
            <div className="space-y-6 mb-8">
              <div className="bg-white p-6 rounded-lg border">
                <div className="flex items-start mb-4">
                  <Camera className="h-6 w-6 text-primary mr-4 mt-1" />
                  <div>
                    <h3 className="text-xl font-semibold mb-2">Photographic Evidence</h3>
                    <p className="text-gray-600 mb-3">
                      High-quality photos and videos are the foundation of any successful insurance claim. 
                      Our professional documentation captures damage from multiple angles with proper lighting and detail.
                    </p>
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <h4 className="font-medium mb-2">Our Photo Documentation Includes:</h4>
                      <div className="grid md:grid-cols-2 gap-4">
                        <ul className="text-sm space-y-1">
                          <li>• Overall roof condition (multiple angles)</li>
                          <li>• Close-up damage details</li>
                          <li>• Interior damage (if applicable)</li>
                          <li>• Gutter and fascia damage</li>
                          <li>• Surrounding property damage</li>
                        </ul>
                        <ul className="text-sm space-y-1">
                          <li>• Attic inspection photos</li>
                          <li>• Structural damage evidence</li>
                          <li>• HVAC system damage</li>
                          <li>• Comparison photos (when available)</li>
                          <li>• Drone aerial photography</li>
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-white p-6 rounded-lg border">
                <div className="flex items-start mb-4">
                  <Calendar className="h-6 w-6 text-primary mr-4 mt-1" />
                  <div>
                    <h3 className="text-xl font-semibold mb-2">Weather Event Documentation</h3>
                    <p className="text-gray-600 mb-3">
                      Establishing the date and severity of the weather event is crucial for claim success. 
                      We research and compile comprehensive weather data to support your claim.
                    </p>
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <h4 className="font-medium mb-2">Weather Documentation Sources:</h4>
                      <div className="grid md:grid-cols-2 gap-4">
                        <ul className="text-sm space-y-1">
                          <li>• National Weather Service reports</li>
                          <li>• Local weather station data</li>
                          <li>• Radar and satellite imagery</li>
                          <li>• Storm tracking information</li>
                        </ul>
                        <ul className="text-sm space-y-1">
                          <li>• Hail size and wind speed reports</li>
                          <li>• Storm path documentation</li>
                          <li>• Neighboring damage reports</li>
                          <li>• Media coverage of the event</li>
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-white p-6 rounded-lg border">
                <div className="flex items-start mb-4">
                  <Ruler className="h-6 w-6 text-primary mr-4 mt-1" />
                  <div>
                    <h3 className="text-xl font-semibold mb-2">Detailed Measurements & Assessments</h3>
                    <p className="text-gray-600 mb-3">
                      Accurate measurements and technical assessments provide the foundation for proper 
                      construction estimates and ensure complete restoration of your property.
                    </p>
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <h4 className="font-medium mb-2">Our Technical Assessment Includes:</h4>
                      <div className="grid md:grid-cols-2 gap-4">
                        <ul className="text-sm space-y-1">
                          <li>• Precise roof measurements</li>
                          <li>• Pitch and slope calculations</li>
                          <li>• Square footage calculations</li>
                          <li>• Linear footage for trim work</li>
                        </ul>
                        <ul className="text-sm space-y-1">
                          <li>• Structural damage assessment</li>
                          <li>• Code compliance evaluation</li>
                          <li>• Material specification requirements</li>
                          <li>• Construction methodology planning</li>
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-white p-6 rounded-lg border">
                <div className="flex items-start mb-4">
                  <MapPin className="h-6 w-6 text-primary mr-4 mt-1" />
                  <div>
                    <h3 className="text-xl font-semibold mb-2">Property Information & History</h3>
                    <p className="text-gray-600 mb-3">
                      Understanding your property's history and current condition helps establish 
                      the extent of storm damage versus pre-existing conditions.
                    </p>
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <h4 className="font-medium mb-2">Property Documentation Includes:</h4>
                      <div className="grid md:grid-cols-2 gap-4">
                        <ul className="text-sm space-y-1">
                          <li>• Property age and construction details</li>
                          <li>• Previous roofing work history</li>
                          <li>• Permit records (when available)</li>
                          <li>• Prior insurance claims</li>
                        </ul>
                        <ul className="text-sm space-y-1">
                          <li>• Current material specifications</li>
                          <li>• Building code requirements</li>
                          <li>• HOA or municipal requirements</li>
                          <li>• Energy efficiency considerations</li>
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <h2 className="text-2xl font-bold mb-4">Advanced Documentation Techniques</h2>
            
            <div className="grid md:grid-cols-2 gap-6 mb-8">
              <div className="bg-white p-6 rounded-lg border">
                <h3 className="text-xl font-semibold mb-4">Drone Aerial Photography</h3>
                <p className="text-gray-600 mb-3">
                  Professional drone photography provides comprehensive overhead views and access 
                  to areas that are difficult or dangerous to inspect manually.
                </p>
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h4 className="font-medium mb-2">Drone Benefits:</h4>
                  <ul className="text-sm space-y-1">
                    <li>• Complete roof overview</li>
                    <li>• Hard-to-reach area inspection</li>
                    <li>• High-resolution imaging</li>
                    <li>• Safety for inspectors</li>
                    <li>• Multiple angle documentation</li>
                  </ul>
                </div>
              </div>
              
              <div className="bg-white p-6 rounded-lg border">
                <h3 className="text-xl font-semibold mb-4">Moisture Detection Equipment</h3>
                <p className="text-gray-600 mb-3">
                  Advanced moisture detection helps identify hidden water damage that may not 
                  be visible during standard inspections.
                </p>
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h4 className="font-medium mb-2">Detection Capabilities:</h4>
                  <ul className="text-sm space-y-1">
                    <li>• Hidden water intrusion</li>
                    <li>• Structural moisture content</li>
                    <li>• Insulation saturation levels</li>
                    <li>• Potential mold conditions</li>
                    <li>• Thermal imaging analysis</li>
                  </ul>
                </div>
              </div>
            </div>

            <h2 className="text-2xl font-bold mb-4">Documentation Timeline</h2>
            <p className="mb-4">
              Proper timing of documentation is crucial for claim success. Here's our recommended timeline:
            </p>
            
            <div className="space-y-4 mb-8">
              <div className="bg-white p-6 rounded-lg border">
                <div className="flex items-start mb-4">
                  <div className="bg-red-500 text-white rounded-full w-8 h-8 flex items-center justify-center text-sm font-bold mr-4 mt-1">1</div>
                  <div>
                    <h4 className="font-semibold text-lg mb-2">Immediate (Within 24-48 Hours)</h4>
                    <ul className="text-sm space-y-1">
                      <li>• Take preliminary damage photos</li>
                      <li>• Protect property from further damage</li>
                      <li>• Contact insurance carrier to file claim</li>
                      <li>• Schedule professional assessment</li>
                    </ul>
                  </div>
                </div>
              </div>

              <div className="bg-white p-6 rounded-lg border">
                <div className="flex items-start mb-4">
                  <div className="bg-orange-500 text-white rounded-full w-8 h-8 flex items-center justify-center text-sm font-bold mr-4 mt-1">2</div>
                  <div>
                    <h4 className="font-semibold text-lg mb-2">Within One Week</h4>
                    <ul className="text-sm space-y-1">
                      <li>• Complete professional damage assessment</li>
                      <li>• Compile comprehensive photo documentation</li>
                      <li>• Research weather event data</li>
                      <li>• Prepare preliminary damage report</li>
                    </ul>
                  </div>
                </div>
              </div>

              <div className="bg-white p-6 rounded-lg border">
                <div className="flex items-start mb-4">
                  <div className="bg-green-500 text-white rounded-full w-8 h-8 flex items-center justify-center text-sm font-bold mr-4 mt-1">3</div>
                  <div>
                    <h4 className="font-semibold text-lg mb-2">Before Adjuster Meeting</h4>
                    <ul className="text-sm space-y-1">
                      <li>• Finalize detailed documentation package</li>
                      <li>• Prepare construction estimate</li>
                      <li>• Organize evidence presentation</li>
                      <li>• Schedule adjuster meeting</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-yellow-50 border-l-4 border-yellow-500 p-6 mb-8">
              <h3 className="text-lg font-semibold text-yellow-800 mb-3">
                <AlertCircle className="inline h-5 w-5 mr-2" />
                Common Documentation Mistakes to Avoid
              </h3>
              <div className="grid md:grid-cols-2 gap-4 text-yellow-700">
                <div>
                  <h4 className="font-medium mb-2">Photography Errors:</h4>
                  <ul className="text-sm space-y-1">
                    <li>• Poor lighting or blurry images</li>
                    <li>• Insufficient coverage of damage</li>
                    <li>• No reference points for scale</li>
                    <li>• Missing overall context shots</li>
                  </ul>
                </div>
                <div>
                  <h4 className="font-medium mb-2">Documentation Gaps:</h4>
                  <ul className="text-sm space-y-1">
                    <li>• Incomplete weather event correlation</li>
                    <li>• Missing interior damage assessment</li>
                    <li>• Inadequate measurement documentation</li>
                    <li>• Delayed damage documentation</li>
                  </ul>
                </div>
              </div>
            </div>

            <h2 className="text-2xl font-bold mb-4">Authoritative Citations Section</h2>
            
            <div className="bg-gray-50 p-6 rounded-lg mb-8">
              <h3 className="text-lg font-semibold mb-4">Authoritative Sources & Standards</h3>
              <p className="text-sm text-gray-600 mb-4">
                Documentation standards and requirements are established by these authoritative organizations:
              </p>
              <div className="grid md:grid-cols-2 gap-4 text-sm">
                <div>
                  <h4 className="font-medium mb-2">Professional Standards:</h4>
                  <ul className="space-y-1">
                    <li>
                      <a href="https://www.fema.gov/sites/default/files/2020-07/fema_p-55_public-assistance-debris-management-guide.pdf" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                        FEMA Damage Assessment Guidelines
                      </a>
                    </li>
                    <li>
                      <a href="https://www.nist.gov/engineering-laboratory/smart-connected-systems-division/intelligent-systems-division-programs/disaster" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                        NIST Disaster Resilience Standards
                      </a>
                    </li>
                    <li>
                      <a href="https://www.weather.gov/safety/flood-after" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                        National Weather Service Damage Reports
                      </a>
                    </li>
                    <li>
                      <a href="https://www.asce.org/publications-and-news/civil-engineering-source/civil-engineering-magazine/issues/magazine-issue/article/2020/03/inspecting-buildings-after-disasters" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                        ASCE Post-Disaster Inspection Guidelines
                      </a>
                    </li>
                  </ul>
                </div>
                <div>
                  <h4 className="font-medium mb-2">Industry Resources:</h4>
                  <ul className="space-y-1">
                    <li>
                      <a href="https://www.iii.org/article/tips-for-filing-insurance-claims" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                        Insurance Information Institute Claims Guide
                      </a>
                    </li>
                    <li>
                      <a href="https://www.naic.org/documents/consumer_guide_auto_home.pdf" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                        NAIC Home Insurance Guide
                      </a>
                    </li>
                    <li>
                      <a href="https://www.redcross.org/get-help/how-to-prepare-for-emergencies/types-of-emergencies/home-fire" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                        American Red Cross Disaster Recovery
                      </a>
                    </li>
                    <li>
                      <a href="https://www.ready.gov/financial-preparedness" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                        Ready.gov Financial Preparedness Guide
                      </a>
                    </li>
                  </ul>
                </div>
              </div>
            </div>

            <h2 className="text-2xl font-bold mb-4">Professional Documentation Report</h2>
            <p className="mb-4">
              Our comprehensive documentation report includes all necessary elements for a successful claim:
            </p>
            
            <div className="grid md:grid-cols-2 gap-6 mb-8">
              <div className="bg-green-50 p-6 rounded-lg">
                <CheckCircle className="h-8 w-8 text-green-500 mb-4" />
                <h3 className="text-lg font-semibold mb-3">Report Contents</h3>
                <ul className="space-y-2 text-sm">
                  <li>• Executive summary of damage findings</li>
                  <li>• Detailed photo documentation with descriptions</li>
                  <li>• Weather event correlation analysis</li>
                  <li>• Comprehensive damage inventory</li>
                  <li>• Construction methodology recommendations</li>
                  <li>• Material specifications and quantities</li>
                  <li>• Code compliance requirements</li>
                  <li>• Professional cost estimates</li>
                </ul>
              </div>
              
              <div className="bg-blue-50 p-6 rounded-lg">
                <FileText className="h-8 w-8 text-blue-500 mb-4" />
                <h3 className="text-lg font-semibold mb-3">Delivery Options</h3>
                <ul className="space-y-2 text-sm">
                  <li>• Digital report with high-resolution images</li>
                  <li>• Organized by damage categories</li>
                  <li>• Searchable PDF format</li>
                  <li>• Cloud-based access for easy sharing</li>
                  <li>• Hard copy available upon request</li>
                  <li>• Direct submission to insurance carrier</li>
                  <li>• Updates and revisions as needed</li>
                  <li>• Ongoing technical support</li>
                </ul>
              </div>
            </div>

            <div className="text-center mt-12">
              <h2 className="text-2xl font-bold mb-4">Get Professional Documentation</h2>
              <p className="text-lg text-muted-foreground mb-6">
                Ensure your insurance claim has the strongest possible foundation with our comprehensive documentation services.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button asChild size="lg">
                  <Link to="/contact">Schedule Assessment</Link>
                </Button>
                <Button variant="outline" size="lg" asChild>
                  <Link to="/resources">
                    <FileText className="mr-2 h-5 w-5" />
                    More Resources
                  </Link>
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default DocumentationRequirementsPage;
